import React, { useEffect, useMemo, useState } from 'react';
import ProtectedRoute from '../components/ProtectedRoute';
import ConfirmModal from '../components/ConfirmModal.jsx';
import { useAuth } from '../contexts/AuthContext';
import { videoBackgroundsAPI } from '../services/api.jsx';
import './VideoBackgrounds.css';

const placementPresets = [
  { id: 'wall-mounted', label: 'Wall-mounted (beta)' },
  { id: 'standard-logo', label: 'Standard Logo' }
];

const VideoBackgrounds = () => {
  const { user, hasAnyPermission } = useAuth();
  const [logos, setLogos] = useState([]);
  const [selectedLogoKey, setSelectedLogoKey] = useState('RSC');
  const [selectedPlacementPreset, setSelectedPlacementPreset] = useState('standard-logo');
  const [selectedSourceFile, setSelectedSourceFile] = useState(null);
  const [selectedSourcePreviewUrl, setSelectedSourcePreviewUrl] = useState('');
  const [sharedImages, setSharedImages] = useState([]);
  const [selectedSharedName, setSelectedSharedName] = useState('');
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyingLogo, setApplyingLogo] = useState(false);
  const [uploadingShared, setUploadingShared] = useState(false);
  const [downloadingShared, setDownloadingShared] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeleteBlobName, setPendingDeleteBlobName] = useState('');
  const [pendingDeleteScope, setPendingDeleteScope] = useState('mine');
  const [generatorEnabled, setGeneratorEnabled] = useState(true);
  const [updatingGeneratorVisibility, setUpdatingGeneratorVisibility] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => () => {
    if (selectedSourcePreviewUrl) {
      URL.revokeObjectURL(selectedSourcePreviewUrl);
    }
  }, [selectedSourcePreviewUrl]);

  const roleList = useMemo(
    () => String(user?.role || '')
      .split(',')
      .map(role => role.trim().toLowerCase())
      .filter(Boolean),
    [user?.role]
  );

  const canManageShared = hasAnyPermission(['admin:users:read', 'admin:users:write']);
  const isSuperuser = roleList.includes('superuser');

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [logoRes, mineRes, sharedRes, settingsRes] = await Promise.all([
        videoBackgroundsAPI.getLogos(),
        videoBackgroundsAPI.getMine(),
        videoBackgroundsAPI.getShared(),
        videoBackgroundsAPI.getSettings()
      ]);

      setLogos(logoRes.data?.logos || []);
      setImages(mineRes.data?.images || []);
      const nextShared = sharedRes.data?.images || [];
      setSharedImages(nextShared);
      setGeneratorEnabled(Boolean(settingsRes.data?.generatorEnabled ?? true));
      setSelectedSharedName(prev => {
        if (prev && nextShared.some(item => item.name === prev)) {
          return prev;
        }
        return nextShared[0]?.name || '';
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load video background data');
    } finally {
      setLoading(false);
    }
  };

  const selectedLogo = useMemo(
    () => logos.find(item => item.key === selectedLogoKey),
    [logos, selectedLogoKey]
  );

  const selectedSharedImage = useMemo(
    () => sharedImages.find(item => item.name === selectedSharedName) || null,
    [sharedImages, selectedSharedName]
  );

  const handleSourceImageSelect = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) return;

    if (selectedSourcePreviewUrl) {
      URL.revokeObjectURL(selectedSourcePreviewUrl);
    }

    setSelectedSourceFile(file);
    setSelectedSourcePreviewUrl(URL.createObjectURL(file));
    event.target.value = '';
  };

  const clearSelectedSource = () => {
    if (selectedSourcePreviewUrl) {
      URL.revokeObjectURL(selectedSourcePreviewUrl);
    }
    setSelectedSourcePreviewUrl('');
    setSelectedSourceFile(null);
  };

  const handleApplyLogo = async () => {
    if (!selectedSourceFile || !selectedLogoKey) {
      setError('Please upload an image and select a logo.');
      return;
    }

    try {
      setApplyingLogo(true);
      setError('');
      setMessage('');

      const response = await videoBackgroundsAPI.applyLogo(selectedSourceFile, {
        logoKey: selectedLogoKey,
        placementPreset: selectedPlacementPreset
      });

      const image = response.data?.image;
      if (image) {
        setImages(prev => [image, ...prev]);
      }
      setMessage('Logo applied and saved to your private CDN path.');
      clearSelectedSource();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to apply logo to image');
    } finally {
      setApplyingLogo(false);
    }
  };

  const handleSharedUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploadingShared(true);
      setError('');
      setMessage('');

      const response = await videoBackgroundsAPI.uploadShared(file);
      const image = response.data?.image;
      if (image) {
        setSharedImages(prev => [image, ...prev]);
        setSelectedSharedName(image.name || '');
      }

      setMessage('Shared image uploaded. Users can now select and download it.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload shared image');
    } finally {
      setUploadingShared(false);
      event.target.value = '';
    }
  };

  const handleDownloadSelectedShared = async () => {
    if (!selectedSharedImage?.url || downloadingShared) {
      return;
    }

    try {
      setDownloadingShared(true);
      setError('');
      const response = await fetch(selectedSharedImage.url);
      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      const fileName = (selectedSharedImage.name || 'video-background').split('/').pop() || 'video-background';
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      setError(err.message || 'Failed to download selected image');
    } finally {
      setDownloadingShared(false);
    }
  };

  const handleDelete = async (blobName) => {
    if (!blobName) return;
    setPendingDeleteScope('mine');
    setPendingDeleteBlobName(blobName);
    setShowDeleteConfirm(true);
  };

  const handleDeleteShared = async (blobName) => {
    if (!blobName || !canManageShared) return;
    setPendingDeleteScope('shared');
    setPendingDeleteBlobName(blobName);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (deleting) return;
    if (!pendingDeleteBlobName) return;

    try {
      setDeleting(true);
      setError('');
      setMessage('');
      if (pendingDeleteScope === 'shared') {
        await videoBackgroundsAPI.deleteShared(pendingDeleteBlobName);
        setSharedImages(prev => {
          const remaining = prev.filter(item => item.name !== pendingDeleteBlobName);
          setSelectedSharedName(current => (
            current === pendingDeleteBlobName ? (remaining[0]?.name || '') : current
          ));
          return remaining;
        });
        setMessage('Shared background deleted.');
      } else {
        await videoBackgroundsAPI.deleteMine(pendingDeleteBlobName);
        setImages(prev => prev.filter(item => item.name !== pendingDeleteBlobName));
        setMessage('Background deleted.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete background');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
      setPendingDeleteBlobName('');
      setPendingDeleteScope('mine');
    }
  };

  const handleToggleGeneratorVisibility = async () => {
    if (!isSuperuser || updatingGeneratorVisibility) return;

    const nextEnabled = !generatorEnabled;
    try {
      setUpdatingGeneratorVisibility(true);
      setError('');
      setMessage('');
      const response = await videoBackgroundsAPI.setGeneratorVisibility(nextEnabled);
      const enabled = Boolean(response.data?.generatorEnabled);
      setGeneratorEnabled(enabled);
      setMessage(enabled
        ? 'Background Generator is now visible to all users.'
        : 'Background Generator is now hidden for all users.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update generator visibility');
    } finally {
      setUpdatingGeneratorVisibility(false);
    }
  };

  if (loading) {
    return <div className="container" style={{ padding: '40px', textAlign: 'center' }}>Loading video backgrounds...</div>;
  }

  return (
    <div className="container video-bg-page">
      <ConfirmModal
        show={showDeleteConfirm}
        title={pendingDeleteScope === 'shared' ? 'Delete Shared Background' : 'Delete Background'}
        message={pendingDeleteScope === 'shared'
          ? 'Delete this shared background from the CDN path? This action cannot be undone.'
          : 'Delete this background from your personal CDN path? This action cannot be undone.'}
        onConfirm={confirmDelete}
        onCancel={() => {
          if (deleting) return;
          setShowDeleteConfirm(false);
          setPendingDeleteBlobName('');
          setPendingDeleteScope('mine');
        }}
        confirmText={deleting ? 'Deleting...' : 'Delete'}
        confirmStyle="danger"
      />

      <div className="card">
        <h1>Downloadable Backgrounds</h1>
       
        {canManageShared && (
          <div className="video-bg-actions">
            <label className="btn btn-secondary video-bg-upload-btn">
              {uploadingShared ? 'Uploading Shared Image...' : 'Upload Shared Image'}
              <input type="file" accept="image/*" onChange={handleSharedUpload} hidden disabled={uploadingShared} />
            </label>
          </div>
        )}

        {sharedImages.length === 0 ? (
          <div className="video-bg-empty">No shared backgrounds uploaded yet.</div>
        ) : (
          <>
            <div className="video-bg-image-grid video-bg-select-grid">
              {sharedImages.map((image) => (
                <button
                  type="button"
                  key={image.name || image.url}
                  className={`video-bg-image-card video-bg-select-card ${selectedSharedName === image.name ? 'selected' : ''}`}
                  onClick={() => setSelectedSharedName(image.name)}
                >
                  <img src={image.url} alt="Shared video background" />
                  <div className="video-bg-image-meta">
                    <span>{(image.name || '').split('/').pop() || 'shared image'}</span>
                  </div>
                </button>
              ))}
            </div>
            <div className="video-bg-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleDownloadSelectedShared}
                disabled={!selectedSharedImage || downloadingShared}
              >
                {downloadingShared ? 'Downloading...' : 'Download Selected'}
              </button>
              {canManageShared && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => handleDeleteShared(selectedSharedImage?.name)}
                  disabled={!selectedSharedImage || deleting}
                >
                  Delete Selected
                </button>
              )}
              {selectedSharedImage?.url && (
                <a className="btn btn-secondary" href={selectedSharedImage.url} target="_blank" rel="noreferrer">
                  Open Selected
                </a>
              )}
            </div>
          </>
        )}
      </div>

      {isSuperuser && (
        <div className="card">
          <h2>Generator Visibility (Super-Admin)</h2>
          <p>
            Control whether the Background Generator section is visible for all users.
          </p>
          <div className="video-bg-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleToggleGeneratorVisibility}
              disabled={updatingGeneratorVisibility}
            >
              {updatingGeneratorVisibility
                ? 'Updating...'
                : generatorEnabled
                  ? 'Hide Generator for Everyone'
                  : 'Show Generator for Everyone'}
            </button>
            <span className={`video-bg-toggle-status ${generatorEnabled ? 'enabled' : 'disabled'}`}>
              {generatorEnabled ? 'Currently: Visible' : 'Currently: Hidden'}
            </span>
          </div>
        </div>
      )}

      {generatorEnabled ? (
      <div className="card">
        <h1>Background Builder</h1>
        <p>Upload your own image, choose an RSC logo + placement, then save the branded result to your personal CDN folder.</p>

        {error && <div className="error">{error}</div>}
        {message && <div className="success">{message}</div>}

        <h2 className="video-bg-section-title">1) Upload Background Image</h2>
        <div className="video-bg-actions">
          <label className="btn btn-secondary video-bg-upload-btn">
            {selectedSourceFile ? 'Replace Uploaded Image' : 'Upload Image'}
            <input type="file" accept="image/*" onChange={handleSourceImageSelect} hidden />
          </label>
          {selectedSourceFile && (
            <button type="button" className="btn btn-secondary" onClick={clearSelectedSource}>
              Clear
            </button>
          )}
        </div>
        {selectedSourceFile && (
          <div className="video-bg-upload-preview">
            <div className="video-bg-upload-name">{selectedSourceFile.name}</div>
            {selectedSourcePreviewUrl && <img src={selectedSourcePreviewUrl} alt="Uploaded background preview" />}
          </div>
        )}

        <h2 className="video-bg-section-title">2) Select RSC Logo</h2>
        <div className="video-bg-logo-row">
          <select value={selectedLogoKey} onChange={(e) => setSelectedLogoKey(e.target.value)}>
            {logos.map((logo) => (
              <option key={logo.key} value={logo.key}>{logo.label}</option>
            ))}
          </select>
          {selectedLogo?.url && (
            <img src={selectedLogo.url} alt={selectedLogo.label} className="video-bg-logo-preview" />
          )}
        </div>

        <h2 className="video-bg-section-title">3) Logo Placement Preset</h2>
        <div className="video-bg-logo-row">
          <select value={selectedPlacementPreset} onChange={(e) => setSelectedPlacementPreset(e.target.value)}>
            {placementPresets.map((preset) => (
              <option key={preset.id} value={preset.id}>{preset.label}</option>
            ))}
          </select>
        </div>

        <div className="video-bg-actions">
          <button className="btn btn-primary" onClick={handleApplyLogo} disabled={applyingLogo || !selectedSourceFile}>
            {applyingLogo ? 'Applying Logo...' : 'Apply Logo & Save'}
          </button>
        </div>
      </div>
      ) : (
        <div className="card">
          <h1>Background Generator</h1>
          <div className="video-bg-empty">
            The Background Generator is currently unavailable.
          </div>
        </div>
      )}

      <div className="card">
        <h2>Your Backgrounds</h2>
        <p>Only backgrounds in your own generated/uploaded/branded paths are shown here.</p>

        {images.length === 0 ? (
          <div className="video-bg-empty">No backgrounds yet. Generate or upload one above.</div>
        ) : (
          <div className="video-bg-image-grid">
            {images.map((image) => (
              <div key={image.name || image.url} className="video-bg-image-card">
                <img src={image.url} alt="Video background" />
                <div className="video-bg-image-meta">
                  <span>{image.source || 'image'}</span>
                  <div className="video-bg-image-actions">
                    <a href={image.url} target="_blank" rel="noreferrer">Open</a>
                    <button type="button" className="video-bg-delete-link" onClick={() => handleDelete(image.name)}>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const VideoBackgroundsWithProtection = () => (
  <ProtectedRoute requiredTab="video-backgrounds">
    <VideoBackgrounds />
  </ProtectedRoute>
);

export default VideoBackgroundsWithProtection;
